import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { subscribeToMachine } from '../../state/machine/machineBridge';
import { getRemixMachine } from '../../state/machine/remixFlowController';
import {
	deriveMachineEditorRemixPhase,
	isTerminalEditorRemixPhase,
	type EditorRemixTerminalLifecyclePhase,
} from './editorRemixLifecycle';
import type {
	EditorRemixLifecycleAdapter,
	EditorRemixLifecycleEndReason,
	EditorRemixLifecycleEvent,
	EditorRemixLifecycleHandle,
	EditorRemixMachineLifecycleOwnershipCleanup,
	EditorRemixLifecyclePhase,
	EditorRemixLifecycleStartRequest,
} from './types';

export type EditorRemixLifecycleToken = symbol;

export type RetainedEditorRemixLifecycle = {
	conversationId: string;
	remixId: string;
	token: EditorRemixLifecycleToken;
	/** Returns true only while this retained session is still the owner's current session. */
	isCurrent?: () => boolean;
	/** The first authoritative end reason for this retained flow, even after it is released. */
	getEndReason: () => EditorRemixLifecycleEndReason | undefined;
	/** Returns true only when this call released ownership; a stale or already-settled handle is a no-op. */
	rollback: () => boolean;
	transition: (event: EditorRemixLifecycleEvent) => boolean;
	hasAcceptedPreview: () => boolean;
	/** Returns true only when this call released ownership; a stale or already-settled handle is a no-op. */
	end: (reason: EditorRemixLifecycleEndReason) => boolean;
	/** Releases this session's terminal authority after its terminal UX has settled. */
	settleTerminal: () => boolean;
	setOwnerDestructionEndReason: (reason: EditorRemixLifecycleEndReason | undefined) => void;
};

export type EditorRemixLifecycleOwner = {
	active: RetainedEditorRemixLifecycle | undefined;
	retain: (handle: EditorRemixLifecycleHandle) => RetainedEditorRemixLifecycle | undefined;
	/**
	 * Clears {@link EditorRemixLifecycleRead.hasMachineAuthority} so a new flow's authority is decided
	 * by its own admission. Called by {@link admitAndRetainEditorRemixLifecycle} before admission, so
	 * a flow that fails open cannot inherit the previous flow's authority.
	 *
	 * A no-op while a flow is still retained: that flow owns its terminal effects, so clearing its
	 * authority would unfence the legacy effects it owns. Callers do not need to check first.
	 */
	forgetMachineAuthority: () => void;
};

export type EditorRemixLifecycleRetentionOutcome =
	| { status: 'legacy' }
	| { status: 'retained'; lifecycle: RetainedEditorRemixLifecycle }
	| { status: 'active-flow-rejected' }
	| { status: 'infrastructure-failure' };

const observeAdmission = (
	adapter: EditorRemixLifecycleAdapter | undefined,
	request: EditorRemixLifecycleStartRequest,
	outcome: 'admitted' | 'legacy' | 'rejected' | 'unavailable',
	reason: 'accepted' | 'active-flow' | 'disabled' | 'infrastructure-failure',
): void => {
	try {
		adapter?.observeLifecycle?.({
			actionSubjectId: 'admission',
			producer:
				request.observation?.producer ?? (request.kind === 'create' ? 'create' : 'variant-update'),
			artifactType: request.observation?.artifactType ?? 'unknown',
			...(request.observation?.sentinelSource
				? { sentinelSource: request.observation.sentinelSource }
				: {}),
			outcome,
			reason,
		});
	} catch {
		// Ownership observations must never affect admission behavior.
	}
};

/**
 * Keeps admission and owner retention semantics identical for create and update producers.
 * Infrastructure failures fail open to legacy; a real owner collision fails closed.
 */
export const admitAndRetainEditorRemixLifecycle = ({
	adapter,
	owner,
	request,
}: {
	adapter: EditorRemixLifecycleAdapter | undefined;
	owner: EditorRemixLifecycleOwner | undefined;
	request: EditorRemixLifecycleStartRequest;
}): EditorRemixLifecycleRetentionOutcome => {
	// Every producer funnels through here, so this is the one place that sees a flow start regardless
	// of how it was triggered. Forgetting first means a flow that is disabled, rejected or fails open
	// runs as legacy even when the previous flow was machine-backed.
	//
	// Safe to call unconditionally: a start attempted against a live flow is refused below, and
	// `forgetMachineAuthority` leaves that flow's authority alone because it is still the active one.
	owner?.forgetMachineAuthority();

	if (!adapter) {
		return { status: 'legacy' };
	}

	let admission;
	try {
		admission = adapter.startFlow(request);
	} catch {
		observeAdmission(adapter, request, 'unavailable', 'infrastructure-failure');
		return { status: 'infrastructure-failure' };
	}

	if (admission.status === 'active-flow-rejected') {
		observeAdmission(adapter, request, 'rejected', 'active-flow');
		return { status: 'active-flow-rejected' };
	}
	if (admission.status !== 'admitted') {
		observeAdmission(
			adapter,
			request,
			admission.status === 'shadow-infrastructure-failure' ? 'unavailable' : 'legacy',
			admission.status === 'shadow-infrastructure-failure' ? 'infrastructure-failure' : 'disabled',
		);
		return admission.status === 'shadow-infrastructure-failure'
			? { status: 'infrastructure-failure' }
			: { status: 'legacy' };
	}

	if (!owner) {
		admission.handle.rollback();
		observeAdmission(adapter, request, 'unavailable', 'infrastructure-failure');
		return { status: 'infrastructure-failure' };
	}

	let retained: RetainedEditorRemixLifecycle | undefined;
	try {
		retained = owner.retain(admission.handle);
	} catch {
		admission.handle.rollback();
		observeAdmission(adapter, request, 'unavailable', 'infrastructure-failure');
		return { status: 'infrastructure-failure' };
	}
	if (!retained) {
		admission.handle.rollback();
		observeAdmission(adapter, request, 'rejected', 'active-flow');
		return { status: 'active-flow-rejected' };
	}

	observeAdmission(adapter, request, 'admitted', 'accepted');
	return { status: 'retained', lifecycle: retained };
};

type RetainedEntry = {
	actions: RetainedEditorRemixLifecycle;
	handle: EditorRemixLifecycleHandle;
	onRelease: EditorRemixMachineLifecycleOwnershipCleanup;
	ownerDestructionEndReason?: EditorRemixLifecycleEndReason;
	endReason?: EditorRemixLifecycleEndReason;
	terminalLatched: boolean;
	terminalSettled: boolean;
	machineEnded: boolean;
	token: EditorRemixLifecycleToken;
	/** Releases this flow's machine subscription. Replaced with the real unsubscribe on retention. */
	unsubscribe: () => void;
};

/**
 * The read model of the editor's lifecycle, projected from the machine.
 *
 * Read by the editor plugin's lifecycle mirror and effect host, and by the Remix content component
 * to decide whether a machine session owns this flow's terminal effects.
 */
export type EditorRemixLifecycleRead = Readonly<{
	/** The admitted flow this session describes. Absent until a flow is admitted. */
	remixId: string | undefined;
	/** Whether the machine still owns the flow. False once ownership is released, for any reason. */
	isAdmitted: boolean;
	phase: EditorRemixLifecyclePhase;
	/**
	 * The latched terminal outcome, captured before machine ownership is released.
	 *
	 * Releasing ownership destroys the flow's machine, after which its phase reads as `inactive`, so
	 * a terminal outcome cannot be recovered by re-reading the machine. Latching it here is what
	 * lets terminal UX still see why the flow ended, until it calls `dismissTerminal()`.
	 */
	terminalPhase: EditorRemixTerminalLifecyclePhase | undefined;
	/** Clears the latched outcome. Bound to its own session, so a stale call is a no-op. */
	dismissTerminal: () => void;
	/**
	 * Whether a machine session owns this flow's effects, for the whole flow rather than only while
	 * the session is readable.
	 *
	 * Distinct from `remixId !== undefined` on purpose. Terminal UX dismisses the session as the last
	 * step of its own cleanup, and the legacy effects that UX replaces are not all edge-guarded — they
	 * re-run while their store signal holds. A consumer fencing on the session alone would therefore
	 * re-open the legacy path immediately after the command-owned cleanup finished.
	 *
	 * Latched inside `retain` rather than derived in render, so it is already true in the same commit
	 * a flow admitted into an already-terminal machine settles in. Cleared per flow start by
	 * {@link EditorRemixLifecycleOwner.forgetMachineAuthority}.
	 */
	hasMachineAuthority: boolean;
}>;

/** Empty read model before admission, after a non-terminal release, or after terminal dismissal. */
const NO_EDITOR_REMIX_LIFECYCLE: EditorRemixLifecycleRead = Object.freeze({
	remixId: undefined,
	isAdmitted: false,
	phase: 'inactive',
	terminalPhase: undefined,
	dismissTerminal: () => {},
	hasMachineAuthority: false,
});

/**
 * The read model plus the identity it is scoped to.
 *
 * The token is the retention token, so staleness is judged by exactly the same identity the owner's
 * own action guards use. `remixId` is not a substitute: it is what consumers scope themselves to,
 * not what distinguishes one retention of it from another.
 *
 * `hasMachineAuthority` is deliberately excluded: it outlives the session that latched it, so the
 * provider holds it separately and merges it into the read value.
 */
type EditorRemixLifecycleSession = Omit<EditorRemixLifecycleRead, 'hasMachineAuthority'> & {
	token: EditorRemixLifecycleToken;
};

type EditorRemixLifecycleSessionUpdate = Partial<
	Pick<EditorRemixLifecycleSession, 'isAdmitted' | 'phase' | 'terminalPhase'>
>;

const EditorRemixLifecycleOwnerContext = createContext<EditorRemixLifecycleOwner | undefined>(
	undefined,
);

/**
 * Separate from the owner context so machine transitions do not re-render producers holding owner
 * actions, and retention does not re-render read consumers.
 */
const EditorRemixLifecycleReadContext =
	createContext<EditorRemixLifecycleRead>(NO_EDITOR_REMIX_LIFECYCLE);

export const useEditorRemixLifecycleOwner = (): EditorRemixLifecycleOwner | undefined =>
	useContext(EditorRemixLifecycleOwnerContext);

export const useEditorRemixLifecycle = (): EditorRemixLifecycleRead =>
	useContext(EditorRemixLifecycleReadContext);

export const EditorRemixLifecycleOwnerProvider = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	const activeRef = useRef<RetainedEntry | undefined>(undefined);
	const terminalEntryRef = useRef<RetainedEntry | undefined>(undefined);
	const [active, setActive] = useState<RetainedEditorRemixLifecycle | undefined>(undefined);
	/**
	 * The read session, mirrored into a ref so the observer can judge staleness synchronously on a
	 * machine transition rather than a commit later.
	 */
	const sessionRef = useRef<EditorRemixLifecycleSession | undefined>(undefined);
	const [session, setSession] = useState<EditorRemixLifecycleSession | undefined>(undefined);
	/**
	 * Machine authority for the current flow, outliving the session the way the registry's
	 * `terminal-latched` write-authority lease does. Mirrored into a ref so `retain` can set it in the
	 * same synchronous turn it seeds the session, before `observe` can settle a terminal outcome.
	 */
	const hasMachineAuthorityRef = useRef(false);
	const [hasMachineAuthority, setHasMachineAuthority] = useState(false);

	const forgetMachineAuthority = useCallback((): void => {
		if (!hasMachineAuthorityRef.current) {
			return;
		}
		// A live flow keeps its authority. Forgetting is for a flow that has finished: a start
		// attempted against a live flow is refused, and that flow goes on owning its terminal effects,
		// so dropping its authority here would unfence the legacy effects it owns and settle twice.
		//
		// Judged on the ref rather than `active`, which is state and so lags by a commit. `retain`
		// refuses on this same ref, so enforcing it here keeps the two decisions from disagreeing
		// inside a single turn, and means no caller can get this wrong.
		if (activeRef.current) {
			return;
		}
		hasMachineAuthorityRef.current = false;
		setHasMachineAuthority(false);
	}, []);

	const writeSession = useCallback(
		(token: EditorRemixLifecycleToken, update: EditorRemixLifecycleSessionUpdate): void => {
			const current = sessionRef.current;
			// A stale flow cannot alter a newer session, or revive a dismissed one.
			if (current?.token !== token) {
				return;
			}

			const next: EditorRemixLifecycleSession = { ...current, ...update };
			if (
				next.isAdmitted === current.isAdmitted &&
				next.phase === current.phase &&
				next.terminalPhase === current.terminalPhase
			) {
				return;
			}

			sessionRef.current = next;
			setSession(next);
		},
		[],
	);

	const dismissTerminal = useCallback((token: EditorRemixLifecycleToken): void => {
		const current = sessionRef.current;
		// Only a latched outcome is dismissible. Clearing a session whose flow is still running would
		// blind the observer to that flow's own terminal phase and strand machine ownership.
		if (current?.token !== token || current.terminalPhase === undefined) {
			return;
		}
		sessionRef.current = undefined;
		setSession(undefined);
	}, []);

	/**
	 * The lifecycle observer: the only caller of `end('error')` and `end('aborted')`.
	 *
	 * Runs synchronously on the machine's transition, and latches a terminal phase into the session
	 * before releasing machine ownership — the release destroys the machine, so an outcome not
	 * captured here is lost. Repeat and late observations are absorbed by the owner's release guard,
	 * so a terminal `end()` happens exactly once.
	 */
	const observe = useCallback(
		(entry: RetainedEntry): void => {
			const { actions, token } = entry;
			if (sessionRef.current?.token !== token) {
				return;
			}

			const phase = deriveMachineEditorRemixPhase(getRemixMachine(actions.remixId)?.state());
			if (!isTerminalEditorRemixPhase(phase)) {
				writeSession(token, { phase });
				return;
			}

			// This must happen before `end()` releases machine ownership. The product callback keeps
			// lifecycle writes fenced while the existing terminal UX cleanup runs.
			entry.terminalLatched = true;
			entry.endReason ??= phase;
			terminalEntryRef.current = entry;
			try {
				entry.onRelease.latchTerminalAuthority?.();
			} catch {
				// Product presentation cleanup must not strand the machine on a terminal transition.
			}
			writeSession(token, { isAdmitted: false, phase, terminalPhase: phase });
			// Releasing here destroys the machine part-way through the very notification that is
			// still fanning out. `machineBridge` snapshots its subscriber set before iterating, so
			// subscribers registered after this one still run — but they will read no machine and
			// project `inactive` rather than the terminal phase.
			//
			// Any observer that needs to see the terminal phase must therefore subscribe *before*
			// the owner retains the flow. M2 shadow parity relies on this: the product adapter
			// subscribes inside `startFlow`, which runs before `admitAndRetainEditorRemixLifecycle`
			// calls `retain`, so it observes the outcome first. Reordering those two would silently
			// downgrade every terminal parity comparison to `machine-not-tracking`.
			actions.end(phase);
		},
		[writeSession],
	);

	const retain = useCallback(
		(handle: EditorRemixLifecycleHandle): RetainedEditorRemixLifecycle | undefined => {
			if (activeRef.current) {
				return undefined;
			}
			const token = Symbol('editor-remix-lifecycle');
			let hasAcceptedPreview = false;
			let entry: RetainedEntry;
			const isCurrent = () => activeRef.current?.token === token;
			const settleTerminal = (): boolean => {
				if (
					terminalEntryRef.current !== entry ||
					!entry.terminalLatched ||
					!entry.machineEnded ||
					entry.terminalSettled
				) {
					return false;
				}
				let released = true;
				try {
					released = entry.onRelease.settleTerminalAuthority?.() ?? true;
				} catch {
					return false;
				}
				if (!released) {
					return false;
				}
				entry.terminalSettled = true;
				terminalEntryRef.current = undefined;
				return true;
			};
			const release = () => {
				if (!isCurrent()) {
					return false;
				}
				const retained = activeRef.current;
				if (retained?.terminalLatched) {
					retained.machineEnded = true;
				}
				try {
					retained?.onRelease();
				} catch {
					// Product-local cleanup must not prevent lifecycle release or leave ownership stuck.
				}
				activeRef.current = undefined;
				if (!retained?.terminalLatched && terminalEntryRef.current === retained) {
					terminalEntryRef.current = undefined;
				}
				retained?.unsubscribe();
				setActive(undefined);
				const currentSession = sessionRef.current;
				if (currentSession?.token === token && currentSession.terminalPhase === undefined) {
					sessionRef.current = undefined;
					setSession(undefined);
				} else {
					writeSession(token, { isAdmitted: false });
				}
				return true;
			};

			const actions: RetainedEditorRemixLifecycle = {
				conversationId: handle.conversationId,
				remixId: handle.remixId,
				token,
				isCurrent,
				getEndReason: () => entry.endReason,
				rollback: () => {
					if (!release()) {
						return false;
					}
					handle.rollback();
					return true;
				},
				transition: (event) => {
					if (!isCurrent()) {
						return false;
					}
					const accepted = handle.transition?.(event) ?? false;
					if (accepted && event.type === 'PREVIEW_OPENED') {
						hasAcceptedPreview = true;
					}
					if (accepted && event.type === 'INSERTED') {
						release();
					}
					return accepted;
				},
				hasAcceptedPreview: () => isCurrent() && hasAcceptedPreview,
				end: (reason) => {
					if (!isCurrent()) {
						return false;
					}
					entry.endReason ??= reason;
					if (!release()) {
						return false;
					}
					handle.end?.(reason);
					return true;
				},
				settleTerminal,
				setOwnerDestructionEndReason: (reason) => {
					if (isCurrent() && activeRef.current) {
						activeRef.current.ownerDestructionEndReason = reason;
					}
				},
			};

			entry = {
				actions,
				handle,
				onRelease: () => {},
				terminalLatched: false,
				terminalSettled: false,
				machineEnded: false,
				token,
				unsubscribe: () => {},
			};
			activeRef.current = entry;
			setActive(actions);

			// Before `observe`, which can settle a terminal outcome in this same turn for a flow
			// admitted into an already-terminal machine. Authority has to be readable in that commit,
			// or the legacy effects would run alongside the terminal commands exactly once.
			hasMachineAuthorityRef.current = true;
			setHasMachineAuthority(true);

			// Seeding replaces any prior session outright, settled or dismissed: the read model
			// describes the current flow, and there is only ever one.
			const seeded: EditorRemixLifecycleSession = {
				token,
				remixId: handle.remixId,
				isAdmitted: true,
				phase: deriveMachineEditorRemixPhase(getRemixMachine(handle.remixId)?.state()),
				terminalPhase: undefined,
				dismissTerminal: () => dismissTerminal(token),
			};
			sessionRef.current = seeded;
			setSession(seeded);
			try {
				entry.onRelease = handle.onRetained?.({ remixId: handle.remixId, token }) ?? (() => {});
				// A replacement owns the sole current-session slot once its product registration succeeds.
				// Keep an older terminal reference until this point so a failed registration cannot strand
				// that lease on provider destruction.
				terminalEntryRef.current = undefined;
			} catch (error) {
				// Leave no local owner behind; the caller will roll the admitted handle back and fail open.
				// If a terminal A was waiting for UX settlement, a replacement that cannot register must
				// settle A here; otherwise its terminal lease would remain fenced until provider teardown.
				terminalEntryRef.current?.actions.settleTerminal();
				activeRef.current = undefined;
				setActive(undefined);
				sessionRef.current = undefined;
				setSession(undefined);
				// The caller fails this flow open to legacy, so it must not keep machine authority.
				forgetMachineAuthority();
				throw error;
			}

			try {
				entry.unsubscribe = subscribeToMachine(handle.remixId, () => observe(entry));
				// Admission is itself an observation point. A flow admitted into an already-terminal
				// machine has no transition left to wait for, so it must settle now.
				observe(entry);
			} catch (error) {
				release();
				// Same reason as above: a flow this provider could not subscribe runs as legacy.
				forgetMachineAuthority();
				throw error;
			}

			return actions;
		},
		[dismissTerminal, forgetMachineAuthority, observe, writeSession],
	);

	useEffect(
		() => () => {
			const retained = activeRef.current;
			const terminal = terminalEntryRef.current;
			if (retained?.ownerDestructionEndReason) {
				retained.endReason ??= retained.ownerDestructionEndReason;
			}
			activeRef.current = undefined;
			try {
				retained?.onRelease();
			} catch {
				// Product-local cleanup must not prevent owner destruction from releasing lifecycle state.
			}
			retained?.unsubscribe();
			if (retained?.ownerDestructionEndReason) {
				retained.handle.end?.(retained.ownerDestructionEndReason);
			} else {
				retained?.handle.rollback();
			}
			if (terminal && terminal !== retained) {
				terminal.actions.settleTerminal();
			} else if (retained?.terminalLatched) {
				retained.actions.settleTerminal();
			}
		},
		[],
	);

	const value = useMemo(
		() => ({ active, retain, forgetMachineAuthority }),
		[active, forgetMachineAuthority, retain],
	);

	/**
	 * Authority is merged in rather than stored on the session, because it has to survive the
	 * session's own dismissal — see {@link EditorRemixLifecycleRead.hasMachineAuthority}.
	 */
	const read = useMemo<EditorRemixLifecycleRead>(
		() => ({ ...(session ?? NO_EDITOR_REMIX_LIFECYCLE), hasMachineAuthority }),
		[hasMachineAuthority, session],
	);

	return (
		<EditorRemixLifecycleOwnerContext.Provider value={value}>
			<EditorRemixLifecycleReadContext.Provider value={read}>
				{children}
			</EditorRemixLifecycleReadContext.Provider>
		</EditorRemixLifecycleOwnerContext.Provider>
	);
};
